import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HEADER_TITLE, DEPT_LINE1, DEPT_LINE2, BRAND_SUB } from "@/lib/constants";
import {
  LayoutDashboard, HandHeart, Users, Clock, Activity, Images, FileBarChart,
  Settings, School, BarChart3, UserCog, Trophy, Swords, Lightbulb, BookOpen,
  Award, LogOut, Menu, X, Bell, KeyRound, GraduationCap, Briefcase, ShieldCheck, Megaphone, Medal, Send, Inbox, MapPin, UsersRound, Camera, Network,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/api";
import { toast } from "sonner";
import PledgeDialog from "@/components/PledgeDialog";
import NotificationsBell from "@/components/NotificationsBell";
import CoordinatorBell from "@/components/CoordinatorBell";

const coordinatorNav = [
  { to: "/", label: "الرئيسية", icon: LayoutDashboard },
  { to: "/opportunities", label: "الفرص التطوعية", icon: HandHeart },
  { to: "/students/first", label: "طلاب أول ثانوي", icon: GraduationCap },
  { to: "/students/second", label: "طلاب ثاني ثانوي", icon: GraduationCap },
  { to: "/students/third", label: "طلاب ثالث ثانوي", icon: GraduationCap },
  { to: "/staff", label: "الموظفون المتطوعون", icon: Briefcase },
  { to: "/gallery", label: "معرض الأعمال التطوعية", icon: Images },
  { to: "/reports", label: "التقارير", icon: FileBarChart },
  { to: "/my-certificates", label: "سجل الشهادات", icon: Medal },
  { to: "/challenges", label: "التحديات", icon: Swords },
  { to: "/suggestions", label: "المقترحات", icon: Lightbulb },
  { to: "/resources", label: "مكتبة الموارد", icon: BookOpen },
  { to: "/circulars", label: "التعاميم الواردة", icon: Inbox },
  { to: "/settings", label: "إعدادات المدرسة", icon: Settings },
];

const adminNav = [
  { to: "/", label: "لوحة المؤشرات", icon: LayoutDashboard },
  { to: "/schools", label: "المدارس", icon: School },
  { to: "/statistics", label: "الإحصاءات والمقارنات", icon: BarChart3 },
  { to: "/reports", label: "التقارير", icon: FileBarChart },
  { to: "/honor", label: "لوحة الشرف المركزية", icon: Trophy },
  { to: "/challenges", label: "التحديات الأسبوعية", icon: Swords },
  { to: "/monthly-certificates", label: "الشهادات الشهرية", icon: Medal },
  { to: "/instructions", label: "توجيهات المنسقين", icon: Megaphone },
  { to: "/circulars", label: "التعاميم الصادرة", icon: Send },
  { to: "/resources", label: "مكتبة الموارد", icon: BookOpen },
  { to: "/users", label: "إدارة المستخدمين", icon: UserCog },
  { to: "/pledges", label: "سجل التعهّدات", icon: ShieldCheck },
  { to: "/settings", label: "الإعدادات", icon: Settings },
];

function NavItems({ items, onNav }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/"}
          onClick={onNav}
          data-testid={`nav-${it.to.replace("/", "") || "home"}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
              isActive
                ? "bg-gradient-to-l from-sky-500/20 to-sky-400/5 border border-sky-400/50 text-sky-900 font-semibold sky-glow"
                : "text-stone-500 hover:bg-sky-50 hover:text-sky-800 border border-transparent"
            }`
          }
        >
          <it.icon size={19} />
          <span className="flex-1 truncate">{it.label}</span>
          {it.badge > 0 && (
            <span data-testid={`nav-badge-${it.to.replace("/", "") || "home"}`} className="bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center shrink-0">
              {it.badge}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarInner({ items, onNav }) {
  return (
    <div className="h-full flex flex-col py-5">
      <div className="px-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl icon-sky flex items-center justify-center animate-floaty">
            <HandHeart size={24} />
          </div>
          <div className="leading-tight">
            <div className="font-head font-bold text-base gold-text">{HEADER_TITLE}</div>
            <div className="text-[11px] text-stone-500">في مدارس منطقة حائل</div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <NavItems items={items} onNav={onNav} />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, stopImpersonate } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [noCoordCount, setNoCoordCount] = useState(0);
  const [circularsNew, setCircularsNew] = useState(0);
  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/schools-without-coordinator").then((r) => setNoCoordCount((r.data || []).length)).catch(() => {});
    }
  }, [user]);
  const recomputeCirculars = useCallback(() => {
    if (user && user.role !== "admin") {
      api.get("/circulars").then((r) => {
        const seen = localStorage.getItem("circulars_seen_at") || "";
        setCircularsNew((r.data || []).filter((c) => (c.created_at || "") > seen).length);
      }).catch(() => {});
    }
  }, [user]);
  useEffect(() => { recomputeCirculars(); }, [recomputeCirculars]);
  useEffect(() => {
    const h = () => setCircularsNew(0);
    window.addEventListener("circulars-seen", h);
    return () => window.removeEventListener("circulars-seen", h);
  }, []);
  const oversightNav = [{ to: "/supervisors-oversight", label: "متابعة المشرفين", icon: UsersRound },
    ...(user?.is_monitor ? [{ to: "/snapshots", label: "اللقطات المعلوماتية", icon: Camera }]
      : [{ to: "/school-distribution", label: "توزيع المشرفين", icon: Network }])];
  const baseItems = user?.role === "admin"
    ? (user?.is_supervisor && !user?.is_monitor ? [adminNav[0], { to: "/my-schools", label: "متابعة مدارسي", icon: MapPin }, ...adminNav.slice(1)]
      : [adminNav[0], ...oversightNav, ...(user?.is_monitor ? [{ to: "/my-schools", label: "متابعة مدارسي", icon: MapPin }] : []), ...adminNav.slice(1)])
    : coordinatorNav;
  const items = baseItems.map((it) => {
    if (it.to === "/users") return { ...it, badge: noCoordCount };
    if (it.to === "/circulars" && user?.role !== "admin") return { ...it, badge: circularsNew };
    return it;
  });

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("تم تغيير كلمة المرور بنجاح");
      setPwOpen(false);
      setOldPw("");
      setNewPw("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-72 shrink-0 sidebar-sky sticky top-0 h-screen">
        <SidebarInner items={items} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-40 header-sky px-4 sm:px-6 py-3 flex items-center gap-3">
          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden p-2 rounded-lg bg-amber-500/15 border border-amber-400/30 text-amber-700">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-72 sidebar-sky z-[60]">
              <SidebarInner items={items} onNav={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="min-w-0 flex-1 flex items-center gap-3">
            <div className="hidden sm:flex w-11 h-11 rounded-2xl icon-sky items-center justify-center shrink-0">
              <HandHeart size={22} />
            </div>
            <div className="min-w-0">
              <h1 className="font-head font-bold text-base sm:text-lg gold-text truncate">{HEADER_TITLE}</h1>
              <p className="text-[10px] sm:text-xs text-sky-800/70 truncate">{DEPT_LINE1}</p>
              <p className="text-[10px] sm:text-xs text-sky-800/70 truncate">{DEPT_LINE2}</p>
            </div>
          </div>

          {user?.role === "admin" ? <NotificationsBell /> : <CoordinatorBell />}

          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-sm text-stone-800 font-semibold">{user?.name}</span>
            <span className="text-[11px] text-stone-500">{user?.is_supervisor && user?.is_monitor ? "فريق المتابعة · مشرف" : user?.is_supervisor ? "مشرف" : user?.is_monitor ? "فريق المتابعة" : user?.role === "admin" ? "إدارة التعليم" : "منسق التطوع"}</span>
          </div>

          <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="p-2 rounded-lg hover:bg-amber-100/70 text-amber-700 transition" title="تغيير كلمة المرور">
            <KeyRound size={19} />
          </button>
          <button data-testid="logout-btn" onClick={logout} className="p-2 rounded-lg hover:bg-red-500/20 text-red-700 transition" title="تسجيل الخروج">
            <LogOut size={19} />
          </button>
        </header>

        {localStorage.getItem("impersonating") && (
          <div data-testid="impersonation-banner" className="bg-sky-600 text-white text-sm px-4 py-2 flex items-center justify-between gap-3">
            <span>{localStorage.getItem("impersonating_kind") === "supervisor" ? "أنت داخل بحساب المشرف: " : "أنت تتصفّح المدرسة كمنسّق: "}<b>{localStorage.getItem("impersonating")}</b></span>
            <button data-testid="stop-impersonate" onClick={stopImpersonate} className="underline font-semibold whitespace-nowrap">{localStorage.getItem("impersonating_kind") === "supervisor" ? "العودة لحساب فريق المتابعة" : "العودة لحساب المدير"}</button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 max-w-[1500px] w-full mx-auto">{children}</main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <DialogHeader>
            <DialogTitle className="gold-text font-head">تغيير كلمة المرور</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="old-password-input" type="password" placeholder="كلمة المرور الحالية" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="bg-amber-50 border-amber-400/30 text-stone-800" />
            <Input data-testid="new-password-input" type="password" placeholder="كلمة المرور الجديدة" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-amber-50 border-amber-400/30 text-stone-800" />
          </div>
          <DialogFooter>
            <button data-testid="save-password-btn" onClick={changePw} className="btn-gold px-5 py-2 rounded-xl">حفظ</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PledgeDialog />
    </div>
  );
}
